import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { toast } from "sonner"
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { ICCPhoto } from "@/react/hooks/entityHooks/useSwivuCompanyCam";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { getFormattedFileSize } from "@/other/utils/utils";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const checkGrantOrder = ({ canEdit, canView, form, onViewPermissionError }: {
  canEdit: number;
  canView: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: any,
  onViewPermissionError?: (canEdit: number) => void
}) => {
  if (canEdit > canView) {
    if (form) {
      form.setValue("canView", canEdit);
    } else if (onViewPermissionError) {
      onViewPermissionError(canEdit);
    } else {
      toast.error("View permission cannot be higher than Edit permission");
      return false;
    }
  }
  return true;
};


// Helper to check if file is ICCPhoto
const isICCPhoto = (file: BoardFile | ICCPhoto): file is ICCPhoto => {
  return 'uris' in file && Array.isArray(file.uris);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatFile = (files: BoardFile[] | ICCPhoto[], user: any): FormattedFile[] => {
  return files.map((file) => {
    // Handle Company Cam Photo
    if (isICCPhoto(file)) {
      // Find thumbnail URL (prefer annotated version)
      let thumbImageUrl = file.uris.find((uri) => uri.type === 'thumbnail_annotation');
      if (!thumbImageUrl) thumbImageUrl = file.uris.find((uri) => uri.type === 'thumbnail');

      // Find original URL (prefer annotated version)
      let originalImageUrl = file.uris.find((uri) => uri.type === 'original_annotation');
      if (!originalImageUrl) originalImageUrl = file.uris.find((uri) => uri.type === 'original');

      return {
        id: file.id,
        name: String(file.id), // Use ID as name for CC photos
        thumbnailUrl: thumbImageUrl?.url,
        originalImageUrl: originalImageUrl?.url,
        size: getFormattedFileSize(file.byte_size),
        canEdit: false, // CC images cannot be edited
        canView: true, // CC images are always viewable
        addedBy: file.creator_name,
        lastUpdatedBy: undefined,
        lastUpdatedAt: new Date(file.updated_at * 1000).toISOString(),
        isCCImage: true,
        record: null,
        canViewGrant: undefined,
        canEditGrant: undefined,
        age: undefined,
        description: file.description || undefined
      };
    }

    // fallback board file or image or cc document values
    return {
      id: file.id,
      name: (file.name || file.id || "N/A") as string,
      thumbnailUrl: file.thumbnailUrl,
      originalImageUrl: file.url,
      size: getFormattedFileSize(file.size || file.byte_size),
      canEdit: file.canEdit && user?.can(file.canEdit),
      canView: file.canView && user?.can(file.canView),
      addedBy: file.uploadedBy || file.creator_name,
      lastUpdatedBy: file.updatedBy,
      lastUpdatedAt: file.updatedAt || (file.updated_at ? (new Date(file.updated_at * 1000).toISOString()): undefined),
      isCCImage: false,
      record: file,
      canViewGrant: file.canView,
      canEditGrant: file.canEdit,
      age: file.aging,
      description: file.description,
      isAnnotated: file.isAnnotated || false
    };
  });
}
